import { useInfiniteQuery } from '@tanstack/react-query';
import { Select } from 'antd';
import { useState } from 'react';
import { DEBOUNCE_TIME } from '@/std/const/debounceTime';
import { useDebouncedState } from '@/std/hooks/useDebouncedState';
import type { ProdutoCategoria } from '../types/ProdutoCategoria';
import {
    apiProdutoCategoriaConsultarInfiniteQueryOptions,
    type ProdutoCategoriaConsultarParams,
} from '../api/apiProdutoCategoriaConsultar';

export function ProdutoCategoriaSelect({
    value,
    onChange,
    initialOptions,
}: {
    value?: number | null;
    onChange?: (idpk: number | null, categoria: ProdutoCategoria | null) => void;
    initialOptions?: { value: number; label: string }[];
}) {
    const [enabled, setEnabled] = useState(false);
    const [search, setSearch] = useDebouncedState('', DEBOUNCE_TIME);

    const baseParams: ProdutoCategoriaConsultarParams = {
        descricao: search,
        orderby: 'prc_sequencia',
        qtde_registros: 10,
    };

    const { data, isFetching, hasNextPage, fetchNextPage } = useInfiniteQuery({
        ...apiProdutoCategoriaConsultarInfiniteQueryOptions(baseParams),
        enabled,
    });

    const allItems = data?.pages.flat() || [];

    const options = enabled
        ? allItems.map((item) => ({
              label: item.prc_caminho ?? '',
              value: item.prc_idpk,
          }))
        : initialOptions;

    const handleChange = (newValue: number | undefined | null): void => {
        if (!onChange) {
            return;
        }

        const categoria = allItems.find((r) => r.prc_idpk === newValue);
        onChange(newValue || null, categoria || null);
    };

    const handleClick = (): void => {
        if (!enabled) {
            setEnabled(true);
        }
    };

    const handlePopupScroll: React.UIEventHandler<HTMLDivElement> = async (event) => {
        const target = event.target as HTMLDivElement;

        if (
            hasNextPage &&
            !isFetching &&
            target.scrollTop + target.offsetHeight >= target.scrollHeight - 1
        ) {
            await fetchNextPage();
        }
    };

    return (
        <Select
            allowClear={true}
            className='w-full'
            loading={isFetching}
            notFoundContent={isFetching ? 'Pesquisando...' : undefined}
            onChange={handleChange}
            onClick={handleClick}
            onPopupScroll={handlePopupScroll}
            options={options}
            showSearch={{
                filterOption: false,
                onSearch: setSearch,
            }}
            value={value}
        />
    );
}
